import React, { ComponentProps, forwardRef } from 'react'

import styled from 'styled-components'
import { ColorProps, color, DisplayProps, display, SpaceProps, space, variant, WidthProps, width } from 'styled-system'

import { filterStyledSystemProps } from '@/helpers/styled-props'
import { StatusColor } from '@/styles'
import { Theme } from '@/styles/theme'

export enum ColorBoxVariant {
  STATUS = 'STATUS',
  NOTIFICATION = 'NOTIFICATION',
}

const ColorBoxVariants = ({ theme }: { theme: Theme }) =>
  variant({
    prop: '$variant',
    variants: {
      [ColorBoxVariant.STATUS]: {
        fontSize: theme.fonts.fontSize14,
        fontFamily: theme.fonts.textCondensedFont,
      },
      [ColorBoxVariant.NOTIFICATION]: {
        fontSize: theme.fonts.fontSize12,
        fontFamily: theme.fonts.textFont,
      },
    },
  })

type ColorBoxStyledProps = {
  $bgColor?: StatusColor
  $variant?: ColorBoxVariant
} & SpaceProps &
  ColorProps &
  WidthProps &
  DisplayProps

const ColorBoxStyled = styled.div.withConfig({
  shouldForwardProp: filterStyledSystemProps(),
})<ColorBoxStyledProps>`
  box-sizing: border-box;
  padding: 0 6px;
  line-height: 140%;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: ${({ $bgColor, theme }) => ($bgColor ? theme.colors[$bgColor] : 'transparent')};

  ${color}
  ${space}
  ${width}
  ${ColorBoxVariants}
  ${display}
`

type ColorBoxProps = {
  bgColor?: StatusColor
  variant?: ColorBoxVariant
  as?: React.ElementType
} & SpaceProps &
  ColorProps &
  WidthProps &
  DisplayProps &
  Omit<ComponentProps<'div'>, 'color'>

const ColorBox = forwardRef<HTMLDivElement, ColorBoxProps>(({ bgColor, variant = ColorBoxVariant.STATUS, as, ...rest }, ref) => (
  <ColorBoxStyled ref={ref} as={as} $bgColor={bgColor} $variant={variant} {...rest} />
))

ColorBox.displayName = 'ColorBox'

export default ColorBox
